/**
 * Thin client for `/v1/auth/*` endpoints. AuthContext sits on top of
 * this; tests for the endpoints themselves live on the server side.
 *
 * All requests go with `credentials: 'include'` so the session cookie
 * (`__Host-bliksys_session` in prod, `bliksys_session` in dev) flows
 * across the cloud.bliksys.com → api.bliksys.com call.
 */

const DEFAULT_BASE = 'http://localhost:3001';

export interface MeResponse {
  user: { id: string; email: string; name: string | null };
  organizations: { id: string; name: string; slug: string; role: string }[];
  activeOrgId: string | null;
}

export class AuthError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(args: { status: number; code: string; message: string }) {
    super(args.message);
    this.name = 'AuthError';
    this.status = args.status;
    this.code = args.code;
  }
}

export interface AuthApiOptions {
  /** Override `BLIKSYS_API_URL` / localhost default. */
  baseUrl?: string;
}

export function authApi(opts: AuthApiOptions = {}): {
  me(): Promise<MeResponse | null>;
  signOut(): Promise<void>;
  switchOrg(slug: string): Promise<void>;
  createOrg(name: string, slug?: string): Promise<{ id: string; name: string; slug: string }>;
} {
  const baseUrl = opts.baseUrl ?? import.meta.env.BLIKSYS_API_URL ?? DEFAULT_BASE;

  async function call<T>(
    path: string,
    init: RequestInit = {},
    opts: { allow401?: boolean } = {},
  ): Promise<T> {
    const res = await fetch(`${baseUrl}${path}`, {
      credentials: 'include',
      ...init,
    });
    if (opts.allow401 && res.status === 401) return null as T;
    if (!res.ok) {
      let body: { code?: string; message?: string } = {};
      try {
        body = (await res.json()) as typeof body;
      } catch {
        /* fall through */
      }
      throw new AuthError({
        status: res.status,
        code: body.code ?? 'HTTP_ERROR',
        message: body.message ?? res.statusText,
      });
    }
    return (await res.json()) as T;
  }

  return {
    async me(): Promise<MeResponse | null> {
      // /me 401s when not signed in — return null so callers can treat
      // it as "not signed in" without try/catch noise.
      return await call<MeResponse | null>('/v1/auth/me', {}, { allow401: true });
    },
    async signOut(): Promise<void> {
      await call<unknown>('/v1/auth/logout', { method: 'POST' });
    },
    async switchOrg(slug: string): Promise<void> {
      await call<unknown>(`/v1/orgs/${encodeURIComponent(slug)}/switch`, { method: 'POST' });
    },
    async createOrg(
      name: string,
      slug?: string,
    ): Promise<{ id: string; name: string; slug: string }> {
      const res = await call<{ organization: { id: string; name: string; slug: string } }>(
        '/v1/orgs',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, ...(slug !== undefined && { slug }) }),
        },
      );
      return res.organization;
    },
  };
}
